import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { Link, generatePath } from 'react-router';
import {
  pushCampaignModalUpgradeEventToDataLayer,
  pushCampaignModalViewEventToDataLayer,
} from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { euDataIncludedMsg, t } from '../../common/i18n/index.js';
import { getCommercialProductPrices } from '../../common/utils/commercialProductUtils';
import { formatToDDMM } from '../../common/utils/dateUtils.js';
import { centsToEuros, getRecurringPriceAsTextFromDetails } from '../../common/utils/priceUtils.js';
import { getSubscriptionUrlId } from '../../common/utils/subscriptionUtils.js';
import { SalesType } from '../../generated/api/salesType';
import { getPriceString } from '../SubscriptionCard/subscriptionCardUtils';
import type { CampaignAssociation, CommercialProduct, Subscription } from '../../generated/api/models.js';

import './SelfServiceCampaign.scss';

interface CampaignContentProps {
  sub: Subscription;
  commercialProduct: CommercialProduct;
  campaignAssociation: CampaignAssociation;
}

const CampaignContent = ({ sub, commercialProduct, campaignAssociation }: CampaignContentProps) => {
  const originalMonthlyPriceString = getRecurringPriceAsTextFromDetails(sub.details);
  const newPrices = getCommercialProductPrices(commercialProduct, campaignAssociation, SalesType.CHANGE_OFFER);
  const newMonthlyPriceString = getPriceString(newPrices.monthlyRecurringCharge);
  const NEW_EU_DATA = '80';

  const getExpirationDateFormatted = () => {
    if (campaignAssociation.expirationDate) {
      return formatToDDMM(campaignAssociation.expirationDate);
    }
    return '';
  };

  return (
    <div className="self-service-campaign">
      <span className="self-service-campaign__icon self-service-campaign__icon--sim-new" aria-hidden="true" />
      <div className="self-service-campaign__header">
        <h3>{t.CQYP('Upgrade your subscription to the next level')}</h3>
        <h4>{t.EDWL('As thanks for your good customer relationship, we offer you a subscription upgrade benefit:')}</h4>
      </div>

      <p>
        <small className={dsClass.FONT_WEIGHT_600}>{t.TT09('Your current subscription:')}</small>
        <br />
        <b>
          {sub.subscriptionName} ({`${originalMonthlyPriceString}`})
          <br />
          {sub.subscriptionPurposeOfUse}, {sub.subscriptionUserFriendlyId}
        </b>
      </p>
      <p>
        <small className={dsClass.FONT_WEIGHT_600}>{t.W0ZV('Updating to subscription:')}</small>
        <br />
        <b>{commercialProduct.commercialProductName}</b>
      </p>
      <ul>
        <li>{t.A7CI(euDataIncludedMsg, NEW_EU_DATA)}</li>
        <li>{t.M4JY('Unlimited internet in Finland, Nordics and Baltics')}</li>
        <li>{t.MKML('Add-ons: Mobile Answering Service, Travel Internet')}</li>
      </ul>
      <h3 className={dsClass.MARGIN_BOTTOM_0}>{`${newMonthlyPriceString} ${t.YO7F('{}/month', '€')}`}</h3>
      <small className={dsClass.FONT_WEIGHT_600}>{t.O6XH('No change fee, VAT 0 %')}</small>

      <p>
        {t.TIK0('The subscription has a 24-month fixed-term contract.')}
        <br />
        {t.TIK1('Act soon - benefit valid until {}.', getExpirationDateFormatted())}
      </p>

      <Link to={generatePath(paths.PS_MOBILE_SUBSCRIPTION_NEW)} target="_blank" rel="noreferrer">
        {t.NT3S('See subscription additional information')}
      </Link>

      <p>{t.JGDD('Confirm the update with the button below.')}</p>
    </div>
  );
};

const CampaignConfirmation = ({ sub, mdmId }: { sub: Subscription; mdmId: string }) => {
  return (
    <div className="self-service-campaign">
      <span className="self-service-campaign__icon self-service-campaign__icon--sim-new" aria-hidden="true" />
      <h3>{t.Z4KH('Thank you for the update!')}</h3>

      <p>
        {t.H92N('Subscription update order is being processed. You can track the status of the update ')}
        <Link
          to={
            generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(sub) }) +
            `?mdmId=${mdmId}`
          }
          rel="noreferrer"
        >
          {t.BBNI('here')}
        </Link>
        {t.OVFJ('. Your subscription will be updated within one business day.')}
      </p>
    </div>
  );
};

interface UpgradeButtonProps {
  handleClick: () => void;
  loading: boolean;
  error?: boolean;
  sub: Subscription;
  mdmId: string;
}

const UpgradeButton = ({ handleClick, loading, error, sub, mdmId }: UpgradeButtonProps) => {
  return (
    <>
      {error && (
        <CL.Notification
          type="error"
          text={t.ZMA5('Subscription update failed. You can also update the subscription ')}
          link={{
            href:
              generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(sub) }) +
              `?mdmId=${mdmId}`,
            text: t.HW70('here'),
          }}
        />
      )}
      {loading ? (
        <CL.LoadingSpinner />
      ) : (
        <CL.Button color="primary" onClick={handleClick}>
          {t.U5SS('Confirm update')}
        </CL.Button>
      )}
    </>
  );
};

interface CampaignModalProps {
  subscription: Subscription;
  commercialProduct: CommercialProduct;
  campaignAssociation: CampaignAssociation;
  onSubmit: () => Promise<void>;
  mdmId: string;
}

export const CampaignModal = ({
  subscription,
  commercialProduct,
  campaignAssociation,
  onSubmit,
  mdmId,
}: CampaignModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const analyticsRef = useRef(false);

  useEffect(() => {
    if (analyticsRef.current) {
      return;
    }
    pushCampaignModalViewEventToDataLayer(
      'form_view',
      subscription.subscriptionName,
      commercialProduct.commercialProductName
    );
    analyticsRef.current = true;
  }, [subscription, commercialProduct, campaignAssociation]);

  const handleSubmit = async () => {
    setLoading(true);
    const monthlyRecurringCharge = subscription.details?.monthlyRecurringCharge;
    const newMonthlyRecurringCharge = getCommercialProductPrices(
      commercialProduct,
      campaignAssociation,
      SalesType.CHANGE_OFFER
    ).monthlyRecurringCharge;
    if (monthlyRecurringCharge === undefined || newMonthlyRecurringCharge === undefined) {
      setError(true);
      setLoading(false);
      return;
    }
    try {
      await onSubmit();
      pushCampaignModalUpgradeEventToDataLayer(
        'subscription_change',
        subscription.subscriptionName,
        commercialProduct.commercialProductName,
        centsToEuros(monthlyRecurringCharge),
        centsToEuros(newMonthlyRecurringCharge)
      );
      setConfirmed(true);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const upgradeButton = (
    <UpgradeButton
      loading={loading}
      error={error}
      handleClick={handleSubmit}
      sub={subscription}
      mdmId={mdmId}
      key="upgrade_cta"
    />
  );
  const closeButton = (
    <CL.Button
      key="close_cta"
      onClick={() => {
        setIsOpen(false);
      }}
    >
      {t.I4X2('Close window')}
    </CL.Button>
  );

  return (
    <>
      {isOpen && (
        <CL.Modal
          id="mobile-subscription-campaign-modal"
          size="m"
          autoOpen={true}
          buttons={confirmed ? [closeButton] : [upgradeButton]}
        >
          {!confirmed ? (
            <CampaignContent
              sub={subscription}
              commercialProduct={commercialProduct}
              campaignAssociation={campaignAssociation}
            />
          ) : (
            <CampaignConfirmation sub={subscription} mdmId={mdmId} />
          )}
        </CL.Modal>
      )}
    </>
  );
};
